import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LOGO_URL } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  Loader2, 
  ArrowRight, 
  ArrowLeft,
  CheckCircle2, 
  AlertTriangle,
  Building2,
  User,
  Mail,
  Phone,
  Globe,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { QuestionCard } from '../components/assessment/QuestionCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GuestAssessment = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  // Invitation & Assessment data
  const [invitation, setInvitation] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [questions, setQuestions] = useState(null);
  
  // Form state
  const [step, setStep] = useState('info'); // 'info', 'assessment'
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
    company_name: '',
    phone: ''
  });
  
  // Assessment state
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guest/validate/${token}`);
      const data = response.data;
      
      setInvitation(data.invitation);
      
      // Pre-fill guest info from invitation
      setGuestInfo({
        name: data.invitation.name || '',
        email: data.invitation.email || '',
        company_name: data.invitation.company_name || '',
        phone: ''
      });
      
      // If assessment already started, load it
      if (data.existing_assessment) {
        setAssessment(data.existing_assessment);
        setAnswers(data.existing_assessment.answers || {});
        setCurrentCategoryIndex(data.existing_assessment.current_category_index || 0);
        setStep('assessment');
        await loadQuestions();
      }
      
    } catch (err) {
      if (err.response?.status === 410) {
        setError(err.response.data.detail || 'This invitation has expired or already been completed.');
      } else if (err.response?.status === 404) {
        setError('Invalid invitation link. Please contact Privicy Advisory Services.');
      } else {
        setError('Unable to validate invitation. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/guest/questions/${token}`);
      setQuestions(response.data);
    } catch (err) {
      toast.error('Failed to load assessment questions');
    }
  };

  const handleStartAssessment = async (e) => {
    e.preventDefault();
    
    if (!guestInfo.name || !guestInfo.email || !guestInfo.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/guest/start/${token}`, guestInfo);
      setAssessment(response.data);
      await loadQuestions();
      setStep('assessment');
      toast.success('Assessment started! You can save and resume at any time.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswer = (category, questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [category]: {
        ...(prev[category] || {}),
        [questionId]: answer
      }
    }));
  };

  const saveProgress = async () => {
    if (!assessment) return;
    
    const answersList = [];
    for (const [category, categoryAnswers] of Object.entries(answers)) {
      for (const [questionId, answer] of Object.entries(categoryAnswers)) {
        answersList.push({ category, question_id: questionId, answer });
      }
    }
    
    try {
      await axios.post(`${API_URL}/api/guest/answers/${token}`, {
        assessment_id: assessment.id,
        answers: answersList
      });
    } catch {
      // Silent fail — auto-save will retry on next category
    }
  };

  const handleNextCategory = async () => {
    await saveProgress();
    
    if (currentCategoryIndex < questions.categories.length - 1) {
      setCurrentCategoryIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevCategory = () => {
    if (currentCategoryIndex > 0) {
      setCurrentCategoryIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = async () => {
    await saveProgress();
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/guest/complete/${token}`);
      navigate('/guest/thank-you', { 
        state: { 
          name: guestInfo.name,
          email: guestInfo.email,
          jurisdiction: invitation?.jurisdiction_info?.name
        } 
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#3B82F6] mx-auto mb-4" />
          <p className="text-[#64748B]">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-slate-200 shadow-lg">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-[#0F172A] mb-2">Unable to Access Assessment</h2>
            <p className="text-[#64748B] mb-6">{error}</p>
            <p className="text-sm text-[#64748B]">
              Contact us at <a href="mailto:info@myprivicy.com" className="text-[#3B82F6]">info@myprivicy.com</a> for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Guest Info Form
  if (step === 'info') {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="max-w-2xl mx-auto flex items-center justify-center">
            <img src={LOGO_URL} alt="Privacy Pulse" className="h-8" />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-6 py-12">
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 bg-[#3B82F6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-[#3B82F6]" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#0F172A]">
                Data Protection Compliance Assessment
              </CardTitle>
              <CardDescription className="text-base">
                {invitation?.jurisdiction_info?.name} - {invitation?.jurisdiction_info?.law_name}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="p-6">
              <div className="bg-[#3B82F6]/5 border border-[#3B82F6]/20 rounded-lg p-4 mb-4">
                <p className="text-sm text-[#0F172A]">
                  You've been invited by <strong>Privicy Advisory Services</strong> to complete this compliance assessment. 
                  Please provide your information to begin.
                </p>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-lg p-4 mb-6" data-testid="guest-privacy-notice">
                <p className="text-sm text-[#0F172A] flex items-start gap-2">
                  <Shield className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>How we'll use your information.</strong> Please review our{' '}
                    <a
                      href="https://myprivicy.com/privacy#assessment-notice"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#3B82F6] font-semibold underline hover:text-[#2563EB]"
                      data-testid="privacy-notice-link"
                    >
                      Privacy Notice for Assessments
                    </a>{' '}
                    before you continue.
                  </span>
                </p>
              </div>

              <form onSubmit={handleStartAssessment} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2">
                    <User className="h-4 w-4" /> Full Name *
                  </Label>
                  <Input
                    id="name"
                    value={guestInfo.name}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="John Smith"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={guestInfo.email}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@company.com"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="company" className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" /> Company Name *
                  </Label>
                  <Input
                    id="company"
                    value={guestInfo.company_name}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Acme Corporation"
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="flex items-center gap-2">
                    <Phone className="h-4 w-4" /> Phone Number (Optional)
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={guestInfo.phone}
                    onChange={(e) => setGuestInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                    className="mt-1"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white mt-6"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      Start Assessment
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-[#64748B] text-center mt-6">
                By starting this assessment, you confirm you've read our{' '}
                <a
                  href="https://myprivicy.com/privacy#assessment-notice"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#3B82F6] underline hover:text-[#2563EB]"
                >
                  Privacy Notice for Assessments
                </a>.
                Your responses will be reviewed by Privicy Advisory Services.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Assessment Questions
  if (step === 'assessment' && questions) {
    const currentCategory = questions.categories[currentCategoryIndex];
    const categoryQuestions = questions.questions[currentCategory] || [];
    const categoryLabel = questions.category_labels[currentCategory];
    const categoryDescription = questions.category_descriptions?.[currentCategory];
    
    const totalCategories = questions.categories.length;
    const progress = ((currentCategoryIndex + 1) / totalCategories) * 100;
    
    const isLastCategory = currentCategoryIndex === totalCategories - 1;
    
    // Check if all questions in current category are answered
    const currentAnswers = answers[currentCategory] || {};
    const allAnswered = categoryQuestions.every(q => currentAnswers[q.id]);

    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-3">
              <img src={LOGO_URL} alt="Privacy Pulse" className="h-6" />
              <Badge variant="outline" className="text-[#64748B]">
                {invitation?.jurisdiction_info?.name}
              </Badge>
            </div>
            <div className="flex items-center gap-4">
              <Progress value={progress} className="flex-1 h-2" />
              <span className="text-sm font-medium text-[#64748B] whitespace-nowrap">
                {currentCategoryIndex + 1} / {totalCategories}
              </span>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8">
          {/* Category Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[#0F172A] mb-2">{categoryLabel}</h1>
            {categoryDescription && (
              <p className="text-[#64748B]">{categoryDescription}</p>
            )}
          </div>

          {/* Questions */}
          <div className="space-y-6">
            {categoryQuestions.map((question, idx) => (
              <QuestionCard
                key={question.id}
                question={question}
                index={idx}
                value={currentAnswers[question.id]}
                onChange={(qid, val) => handleAnswer(currentCategory, qid, val)}
                variant="simple"
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-200">
            <Button
              variant="outline"
              onClick={handlePrevCategory}
              disabled={currentCategoryIndex === 0}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>

            {isLastCategory ? (
              <Button
                onClick={handleComplete}
                disabled={submitting || !allAnswered}
                className="bg-[#10B981] hover:bg-[#059669] text-white gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" />
                    Submit Assessment
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNextCategory}
                disabled={!allAnswered}
                className="bg-[#3B82F6] hover:bg-[#2563EB] text-white gap-2"
              >
                Next Category
                <ArrowRight className="h-4 w-4" />
              </Button>
            )}
          </div>

          {!allAnswered && (
            <p className="text-sm text-[#F59E0B] text-center mt-4">
              Please answer all questions before continuing
            </p>
          )}
        </main>
      </div>
    );
  }

  return null;
};

export default GuestAssessment;
